import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import { affPost } from "@/lib/affiliateApi";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Compliance acknowledgment gate. Some program playbooks (Weight
// Management is the first) contain compounded-GLP-1 marketing
// guidance that the FTC + medical-board lens needs the affiliate to
// have read end-to-end before they post. We surface a blocking modal
// the first time the playbook is opened, store the acknowledgment,
// and never show it again.
//
// Trigger conditions (any of):
//   • doc.requiresAcknowledgment === true
//   • doc.complianceWarning is a non-empty string/object
//   • slug matches one of the known high-touch programs and the BE
//     hasn't yet plumbed the explicit flag (forward-compat fallback)
//
// Acknowledgment persistence:
//   • POST /affiliate/content/playbook/{slug}/acknowledge — primary
//   • localStorage `vy9_aff_playbook_ack_{slug}` — fallback so a 404
//     from the BE still respects the user's click and doesn't nag.
const FALLBACK_HIGH_TOUCH_SLUGS = new Set([
  "weight-management",
  "weight",
]);

function lsKey(slug) {
  return `vy9_aff_playbook_ack_${slug}`;
}

// Public helpers — used by the playbook index tile to surface
// "Last acknowledged" + "Re-read playbook" affordances.
export function getLocalAcknowledgment(slug) {
  if (!slug) return null;
  try {
    const raw = localStorage.getItem(lsKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.at ? new Date(parsed.at) : null;
  } catch {
    return null;
  }
}
export function clearLocalAcknowledgment(slug) {
  if (!slug) return;
  try {
    localStorage.removeItem(lsKey(slug));
  } catch {
    /* ignore */
  }
}

export function needsAcknowledgment(doc, slug) {
  if (!doc) return false;
  if (doc.requiresAcknowledgment === true) return true;
  if (doc.complianceWarning) return true;
  if (FALLBACK_HIGH_TOUCH_SLUGS.has((slug || "").toLowerCase())) return true;
  return false;
}

export default function ComplianceGate({ doc, slug, onAcknowledged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!needsAcknowledgment(doc, slug)) {
      onAcknowledged && onAcknowledged();
      return;
    }
    try {
      const ack = localStorage.getItem(lsKey(slug));
      if (ack) {
        onAcknowledged && onAcknowledged();
        return;
      }
    } catch {
      /* localStorage unavailable — fall through to modal */
    }
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, doc]);

  // Lock the page scroll while the modal is open so the playbook
  // doesn't drift behind. Restored on close / unmount.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const accept = async () => {
    setBusy(true);
    // Persist locally first so a BE failure doesn't trap the user in
    // a re-open loop. The BE call is best-effort.
    try {
      localStorage.setItem(
        lsKey(slug),
        JSON.stringify({ at: new Date().toISOString() })
      );
    } catch { /* ignore */ }
    try {
      await affPost(`/affiliate/content/playbook/${slug}/acknowledge`, {
        acknowledgedAt: new Date().toISOString(),
      }, { auth: true });
    } catch (err) {
      // 404 is expected if the BE hasn't shipped the endpoint — we
      // already saved locally, so silently move on. Anything else,
      // surface a toast but still let the user through (they made
      // their choice).
      if (err?.status && err.status !== 404) {
        toast("Saved locally — we'll sync your acknowledgment when the server is reachable.", "info");
      }
    }
    setBusy(false);
    setOpen(false);
    onAcknowledged && onAcknowledged();
  };

  if (!open) return null;

  // The warning body comes from one of three sources, in order of
  // preference: explicit `complianceWarning` (string or {title, body}),
  // a "Part 1" extracted from the playbook markdown, or a generic
  // fallback so the modal never renders empty.
  const warning = resolveWarning(doc);

  // Portal to document.body so the modal's `position: fixed` is
  // relative to the viewport, not whatever transformed/filtered
  // ancestor the playbook reader happens to live inside. Without
  // this, the modal can anchor to a containing block created by a
  // CSS transform and drift below the fold on desktop.
  return createPortal(
    <div
      data-testid="compliance-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-gate-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,14,13,0.78)",
        zIndex: 200,
        display: "flex",
        // Top-align so a tall modal never tucks its top above the fold
        // on shorter viewports.
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "min(72px, 8vh) 20px 20px",
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <article
        style={{
          background: C.cream,
          color: C.ink,
          maxWidth: 720,
          width: "100%",
          maxHeight: "calc(100vh - min(96px, 12vh))",
          overflowY: "auto",
          border: `2px solid ${C.ink}`,
          boxShadow: "0 24px 64px rgba(15,14,13,0.35)",
        }}
      >
        <header style={{ background: C.ink, color: C.cream, padding: "22px 26px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.24em", color: C.peach }}>
            ★ READ THIS BEFORE YOU POST
          </div>
          <h2
            id="compliance-gate-title"
            style={{ margin: "8px 0 0", fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}
          >
            {warning.title}
          </h2>
          <p style={{ marginTop: 8, fontSize: 13, color: "rgba(250,246,240,0.75)", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", lineHeight: 1.55 }}>
            This playbook covers content you can't post the way you would for other programs. Read this section twice.
          </p>
        </header>

        <div
          data-testid="compliance-gate-body"
          style={{ padding: "24px 26px", lineHeight: 1.65, fontSize: 15 }}
        >
          <MarkdownReader source={warning.body_md} testId="compliance-gate-markdown" />
        </div>

        <footer
          style={{
            borderTop: `1px solid ${C.warmGray3}`,
            padding: "18px 26px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            background: C.bone,
            position: "sticky",
            bottom: 0,
          }}
        >
          <p style={{ margin: 0, fontSize: 12, color: C.warmGray, lineHeight: 1.5, maxWidth: 420 }}>
            By continuing you confirm you've read these guidelines and will apply them to every post.
          </p>
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            data-testid="compliance-gate-accept"
            style={{
              padding: "14px 26px",
              background: C.ink,
              color: C.cream,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.22em",
              border: 0,
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "SAVING…" : "I'VE READ THIS — CONTINUE →"}
          </button>
        </footer>
      </article>
    </div>,
    document.body,
  );
}

// Pull a sensible warning body from the doc. Preference:
//   1. doc.complianceWarning {title, body_md} (BE-canonical shape)
//   2. doc.complianceWarning as plain string
//   3. The playbook's "Part 1" section (heuristic: first ##/## block
//      whose heading contains the phrase "read this" or "part 1")
//   4. Generic fallback copy
function resolveWarning(doc) {
  const w = doc?.complianceWarning;
  if (w && typeof w === "object") {
    return {
      title: w.title || "Compliance overview",
      body_md: w.body_md || w.body || "",
    };
  }
  if (typeof w === "string" && w.trim()) {
    return { title: "Compliance overview", body_md: w };
  }
  const md = doc?.body_md || "";
  if (md) {
    const sections = md.split(/\n(?=##\s)/);
    const part1 = sections.find((s) => /^##\s.*(read this section twice|part\s*1|compliance|read first)/i.test(s));
    if (part1) {
      // Drop the leading heading line — the modal supplies its own.
      const body_md = part1.replace(/^##\s.*\n?/, "").trim();
      return { title: extractHeading(part1) || "Compliance overview", body_md };
    }
  }
  return {
    title: "Read before you post",
    body_md:
      "This program has stricter posting rules than most. Until you've read the playbook, copy posts verbatim from the **Sample Posts** library and keep disclaimers intact. Don't share personal medical claims, before/after photos, or specific weight numbers without the disclaimers attached.",
  };
}

function extractHeading(section) {
  const m = section.match(/^##\s+(.+)/);
  return m ? m[1].replace(/[*_`]/g, "").trim() : null;
}
